import React, { useCallback, useState } from "react";
import { toast } from "react-toastify";

import ProjectForm from "../../components/admin/project/ProjectForm";
import ProjectTable from "../../components/admin/project/ProjectTable";

import { useProjectManagement } from "../../hooks/admin/Project/useProjectManagement";

import {
  PROJECT_INITIAL_FORM,
} from "../../constants/admin/project/projectConstants";

import {
  createProjectFormData,
} from "../../utils/admin/projectUtils";

const ProjectManagement = () => {
  const {
    projects,
    loading,
    create,
    update,
    remove,
    uploadPropertyImages,
  } = useProjectManagement();

  const [showForm, setShowForm] = useState(false);
  const [editingProject, setEditingProject] = useState(null);

  const [formData, setFormData] = useState(
    PROJECT_INITIAL_FORM
  );

  const [imageFiles, setImageFiles] = useState([]);

  const [isSubmitting, setIsSubmitting] =
    useState(false);

  const closeModal = useCallback(() => {
    setShowForm(false);
    setEditingProject(null);
    setImageFiles([]);
    setFormData(PROJECT_INITIAL_FORM);
  }, []);

  const handleCreate = useCallback(() => {
    setEditingProject(null);
    setFormData(PROJECT_INITIAL_FORM);
    setImageFiles([]);
    setShowForm(true);
  }, []);

  const handleEdit = useCallback((project) => {
    setEditingProject(project);
    setFormData(createProjectFormData(project));
    setImageFiles([]);
    setShowForm(true);
  }, []);

  const handleDelete = useCallback(
    async (id) => {
      const confirmed = window.confirm(
        "Bạn có chắc chắn muốn xóa dự án này không?"
      );

      if (!confirmed) return;

      try {
        await remove(id);

        toast.success(
          "Xóa dự án thành công!"
        );
      } catch (error) {
        console.error(error);

        toast.error(
          "Không thể xóa dự án"
        );
      }
    },
    [remove]
  );

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const handleImageChange = useCallback((e) => {
    const files = Array.from(
      e.target.files || []
    );

    setImageFiles((prev) => [
      ...prev,
      ...files,
    ]);
  }, []);

  const handleRemoveImage = useCallback(
    (index) => {
      setImageFiles((prev) =>
        prev.filter((_, i) => i !== index)
      );
    },
    []
  );

  const buildPayload = async () => {
    const uploadedImages =
      await uploadPropertyImages(imageFiles);

    return {
      ...formData,
      image:
        uploadedImages.length > 0
          ? uploadedImages
          : Array.isArray(formData.image)
            ? formData.image
            : formData.image
              ? [formData.image]
              : [],
    };
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);

      const payload =
        await buildPayload();

      if (editingProject) {
        await update(
          editingProject.id,
          payload
        );

        toast.success(
          "Cập nhật dự án thành công!"
        );
      } else {
        await create(payload);

        toast.success(
          "Tạo dự án thành công!"
        );
      }

      closeModal();
    } catch (error) {
      console.error(error);

      toast.error(
        "Không thể lưu dự án"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <h2 className="text-xl font-semibold">
          Quản lý dự án
        </h2>

        <button
          onClick={handleCreate}
          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded"
        >
          + Thêm dự án
        </button>
      </div>

      {loading ? (
        <p>Đang tải dữ liệu...</p>
      ) : (
        <ProjectTable
          projects={projects}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      )}

      {showForm && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm overflow-y-auto"
          style={{
            paddingTop: "4rem",
            paddingBottom: "4rem",
          }}
        >
          <div className="relative w-full max-w-3xl bg-white dark:bg-gray-900 rounded-lg shadow-xl mx-4">
            <button
              onClick={closeModal}
              className="absolute top-3 right-4 text-gray-500 hover:text-red-600 dark:hover:text-red-400 text-2xl z-10"
            >
              &times;
            </button>

            <div className="max-h-[85vh] overflow-y-auto p-6">
              <ProjectForm
                formData={formData}
                handleChange={handleChange}
                handleImageChange={
                  handleImageChange
                }
                handleRemoveImage={
                  handleRemoveImage
                }
                handleSubmit={handleSubmit}
                initialData={
                  editingProject
                }
                imageFiles={imageFiles}
                onClose={closeModal}
                isSubmitting={
                  isSubmitting
                }
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectManagement;